import { useCaptureTreeSelectionStore } from "@/components/ui/tree/capture-tree/capture-tree-selection-store";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import {
  OnlyIElementsStore,
  selectIElementsSlice,
} from "@/store/ielements-slice-selector";
import {
  LightweightElement,
  toLightweight,
} from "@/store/lightweight-elements-slice";
import { curryAppSelector } from "@/store/reselect";
import { useAppSelector } from "@/store/store-hooks";
import {
  IElementsState,
  MapWaypointsRendererRef,
  selectAncestor,
  selectIElement,
} from "@faro-lotv/app-component-toolbox";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  isIElementImg2d,
  isIElementImg360,
  isIElementSection,
  isIElementTimeSeriesRoom,
} from "@faro-lotv/ielement-types";
import { createSelector } from "@reduxjs/toolkit";
import { useEffect, useMemo } from "react";

/**
 * Ensures that when a waypoint is hovered in the tree, the corresponding waypoint is highlighted on the 2D overview
 *
 * @param waypointsRenderer Reference to the waypoints renderer
 * @param placeholders List of waypoints currently shown
 */
export function useOnWaypointsSelectedInTree(
  waypointsRenderer: MapWaypointsRendererRef | null,
  placeholders: IElementWaypoint[],
): void {
  const { hoveredIds, selectedIds } = useCaptureTreeSelectionStore();

  const placeholdersTreeElements = useAppSelector(
    selectPlaceholdersTreeElements(placeholders),
  );

  const placeholder2index = useMemo(() => {
    const map = new Map<GUID, number>();
    for (let i = 0; i < placeholdersTreeElements.length; i++) {
      map.set(placeholdersTreeElements[i].id, i);
    }
    return map;
  }, [placeholdersTreeElements]);

  useEffect(() => {
    if (!waypointsRenderer) return;
    try {
      waypointsRenderer.hoveredIds = hoveredIds
        .map((id) => placeholder2index.get(id))
        .filter((index): index is number => index !== undefined);
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      // Out-of-index errors are actively ignored since they can happen e.g.
      // when changing the active area
    }
  }, [hoveredIds, waypointsRenderer, placeholder2index]);

  useEffect(() => {
    if (!waypointsRenderer) return;
    try {
      waypointsRenderer.selectedIds = [...selectedIds]
        .map((id) => placeholder2index.get(id))
        .filter((index): index is number => index !== undefined);
      // eslint-disable-next-line unused-imports/no-unused-vars
    } catch (error) {
      // Out-of-index errors are actively ignored since they can happen e.g.
      // when changing the active area
    }
  }, [selectedIds, waypointsRenderer, placeholder2index]);
}

/**
 * This selector is needed to convert each IElementWaypoint into its corresponding ielement
 * represented in the capture tree, which is normally one of the parent sections.
 *
 * @param state The application root state
 * @param placeholders The list of waypoints being displayed
 * @returns The list of sections represented as tree elements.
 */
const selectPlaceholdersTreeElements = curryAppSelector(
  createSelector(
    [
      selectIElementsSlice,
      (state: OnlyIElementsStore, placeholders: IElementWaypoint[]) =>
        placeholders,
    ],
    computePlaceholdersTreeElements,
  ),
);

function computePlaceholdersTreeElements(
  iElements: IElementsState,
  placeholders: IElementWaypoint[],
): LightweightElement[] {
  const result: LightweightElement[] = [];
  const iElementsState = { iElements };
  for (const placeholder of placeholders) {
    if (isIElementImg360(placeholder) || isIElementImg2d(placeholder)) {
      assert(
        placeholder.parentId,
        "A pano placeholder should always have a parent",
      );
      let parentSection = selectAncestor(
        placeholder,
        isIElementSection,
      )(iElementsState);
      assert(
        parentSection,
        "A pano placeholder should always have a section as parent",
      );
      // if 'parentSection' is a child of a timeseries room, we select the parent of the timeseries room
      // which is the section targeted by project search&filtering
      const grandParent = selectIElement(parentSection.parentId)(
        iElementsState,
      );
      assert(grandParent, "Parents of sections should always exist");
      if (isIElementTimeSeriesRoom(grandParent)) {
        const grandGrandParent = selectIElement(grandParent.parentId)(
          iElementsState,
        );
        assert(
          grandGrandParent && isIElementSection(grandGrandParent),
          "A time travel room should always have a section as parent",
        );
        parentSection = grandGrandParent;
      }
      const lw = toLightweight(parentSection);
      lw.createdAt = placeholder.createdAt;
      result.push(lw);
    } else {
      result.push(toLightweight(placeholder));
    }
  }
  return result;
}
